import Phaser from "phaser";
import AudioManager from "../../../@shared/utils/AudioManager";
import BaseInteriorScene from "../BaseInteriorScene";
import whisperingForestSource from "./whispering-forest-source.json";
import whisperingGrassPng from "../../../@shared/assets/GameMaps/tilesets/forest/TX Tileset Grass.png";
import whisperingPlantPng from "../../../@shared/assets/GameMaps/tilesets/forest/TX Plant.png";
import whisperingPropsPng from "../../../@shared/assets/GameMaps/tilesets/forest/TX Props.png";
import whisperingForestCavePng from "../../../@shared/assets/GameMaps/tilesets/forest/FDR_Grasslands.png";
import forestNpcPng from "../../../@shared/assets/npcs/spritesheets/FD_Character_007.png";
import forestNpcIdlePng from "../../../@shared/assets/npcs/spritesheets/FD_Character_007_Idle.png";
import forestNpc2Png from "../../../@shared/assets/npcs/spritesheets/FD_Character_001.png";
import forestNpc2IdlePng from "../../../@shared/assets/npcs/spritesheets/FD_Character_001_Idle.png";
import forestNpc3Png from "../../../@shared/assets/npcs/spritesheets/FD_Character_002.png";
import forestNpc3IdlePng from "../../../@shared/assets/npcs/spritesheets/FD_Character_002_Idle.png";
import forestNpc4Png from "../../../@shared/assets/npcs/spritesheets/FD_Character_016.png";
import forestNpc4IdlePng from "../../../@shared/assets/npcs/spritesheets/FD_Character_016_Idle.png";
import {
  clearWhisperingForestStoryChestVisit,
  getOrCreateWhisperingForestStoryChestVisit,
  isWhisperingForestStoryPackChestMode,
  markWhisperingForestStoryChestOpened,
} from "./storyPackChests";
import {
  getAvatarSpriteConfig,
  normalizeAvatarName,
  SPRITES_MAP,
  scaleAvatarBodySize,
} from "../../../@shared/utils/SpritesMap";
import { createWalkAnimations, preloadAvatars } from "../../../@shared/utils/SceneHelpers";
import {
  createLocalOrbAvatarImage,
  getLocalOrbAvatarConfig,
  preloadLocalOrbAvatar,
  updateLocalOrbAvatar,
} from "../../../@shared/utils/phaserOrbAvatar";
import { applyWorldSceneBackground } from "../../../@shared/utils/worldSceneBackgrounds";
import { updatePlayerPresence } from "../../../@shared/utils/roomFunctions";
import {
  createStoryCompanionFollower,
  destroyStoryCompanionFollower,
  syncStoryCompanionFollower,
} from "../../../@shared/utils/storyCompanionFollower";
import {
  createStoryRuneStoneTeleportEffect,
  destroyStoryRuneStoneTeleportEffect,
  syncStoryRuneStoneTeleportEffect,
} from "../../../@shared/utils/storyRuneStoneTeleportEffect";
import {
  getStoryCreatureCard,
  STORY_CREATURE_CARDS,
} from "../../../@shared/utils/storyCreatures";
import {
  STORY_FOREST_ENCOUNTERS,
  STORY_FOREST_ENCOUNTER_MODE,
  STORY_FOREST_PURGE_MISSION_ID,
  STORY_FOREST_RUNE_STONE_INTERACTABLE_ID,
  STORY_FOREST_RUNE_STONE_NAME,
  STORY_RUNE_STONE_MISSION_ID,
  isStoryForestPointBlocked,
} from "../../../@shared/utils/storyForestEncounters";
import { WHISPERING_FOREST_PORTAL_RETURN_SPAWN } from "../../../@shared/utils/voidCityStoryPortalSpawns";
import { STORY_WORLD_ENCOUNTER_RELEASE_EVENT } from "../../../@shared/utils/storyWorldEncounterContext";
import { VOID_CAVE_ENTRY_SPAWN } from "../voidCave/voidCaveMapConfig";
import {
  createStoryChestBodies,
  getStoryChestCollisionRect,
  getStoryChestInteractionRect,
} from "../sharedChestCollision";
import { WHISPERING_FOREST_VOID_CAVE_RETURN_SPAWN } from "./whisperingForestMapConfig";

const DOOR_SFX = require("../../../@shared/assets/sounds/door.mp3");
const DEBUG_COLLISIONS = false;
const NPC_FRAME_SIZE = 24;
const NPC_MOVE_SPEED = 0.45;
const NPC_WAIT_MS = 1600;
const NPC_CHAT_BUBBLE_MS = 2600;
const FOREST_AMBIENT_DEPTH = 205;
const VOID_CAVE_PORTAL_LABEL = "voidCavePortal";
const STORY_RUNE_STONE_LABEL = `storyItem:${STORY_FOREST_RUNE_STONE_INTERACTABLE_ID}`;
const STORY_RUNE_STONE_TEXTURE_KEY = "story-lords-rune-stone-card";
const STORY_FOREST_WORLD_NAME = "Whispering Forest";
const STORY_ENCOUNTER_IDLE_WAIT_MS = 900;
const STORY_ENCOUNTER_SCALE = 1.3;
const STORY_ENCOUNTER_SHADOW_ALPHA = 0.24;
const STORY_ENCOUNTER_LABEL_OFFSET_Y = 34;
const STORY_CREATURE_FRAME_SIZE = 24;
const STORY_CREATURE_FRAMES_PER_ROW = 4;
const STORY_CREATURE_ANIMATION_FRAME_RATE = 7;
const STORY_INFINITY_ITEM_WORLD_SCALE = 0.6;
const STORY_INFINITY_ITEM_ICON_SIZE = 38;
const STORY_RUNE_STONE_INTERACT_RANGE = 28;
const STORY_RUNE_STONE_TELEPORT_Y_OFFSET = 18;
const TILE_SIZE = 32;
const TILESET_COLUMNS = 16;
const GROUND_CHUNK_TILES = 32;
const GROUND_CHUNK_PADDING = 128;
const TREE_CULL_PADDING = 224;
const TREE_SWAY_PADDING = 80;
const PREVIEW_LAYER_NAMES = new Set(["BushPreview", "TreePreview", "PropPreview"]);
const MAP_PIXEL_WIDTH = whisperingForestSource.width * whisperingForestSource.tilewidth;
const MAP_PIXEL_HEIGHT = whisperingForestSource.height * whisperingForestSource.tileheight;
const DEFAULT_FOREST_ENTRY_SPAWN = Object.freeze({ x: 512, y: 880 });
const DEFAULT_VOID_CAVE_PORTAL = Object.freeze({
  x: 464,
  y: 88,
  width: 96,
  height: 72,
});
const DEFAULT_STORY_RUNE_STONE_POSITION = Object.freeze({ x: 500, y: 316 });
const TREE_SWAY_SETTINGS = {
  large: { bob: 2.2, duration: 3000 },
  medium: { bob: 1.8, duration: 2700 },
  small: { bob: 1.15, duration: 2600 },
};
const NPC_PATROL_METADATA = [
  {
    key: "wfForestNpc1",
    texture: "wfNpcForestScout",
    idleTexture: "wfNpcForestScoutIdle",
    promptLabel: "Talk To Scout",
    speech: "The trees whisper loudest when the forest is calm.",
    start: { x: 624, y: 104 },
    path: [
      { x: 624, y: 104 },
      { x: 896, y: 104 },
      { x: 896, y: 156 },
      { x: 624, y: 156 },
    ],
  },
  {
    key: "wfForestNpc2",
    texture: "wfNpcForestGuide",
    idleTexture: "wfNpcForestGuideIdle",
    promptLabel: "Talk To Guide",
    speech: "Stay on the open paths and the deeper woods stay friendly.",
    start: { x: 104, y: 276 },
    path: [
      { x: 104, y: 276 },
      { x: 224, y: 276 },
      { x: 224, y: 320 },
      { x: 104, y: 320 },
    ],
  },
  {
    key: "wfForestNpc3",
    texture: "wfNpcForestWanderer",
    idleTexture: "wfNpcForestWandererIdle",
    promptLabel: "Talk To Wanderer",
    speech: "Some chests hide in plain sight. Others wait for curious feet.",
    start: { x: 792, y: 452 },
    path: [
      { x: 792, y: 452 },
      { x: 904, y: 452 },
      { x: 936, y: 560 },
      { x: 808, y: 560 },
    ],
  },
  {
    key: "wfForestNpc4",
    texture: "wfNpcForestRanger",
    idleTexture: "wfNpcForestRangerIdle",
    promptLabel: "Talk To Ranger",
    speech: "If the wind shifts, watch the canopy. It always knows first.",
    start: { x: 460, y: 860 },
    path: [
      { x: 460, y: 860 },
      { x: 540, y: 860 },
      { x: 540, y: 924 },
      { x: 460, y: 924 },
    ],
  },
];

const TREE_TEXTURE_RECTS = {
  large: { x: 24, y: 14, width: 113, height: 139 },
  medium: { x: 161, y: 17, width: 96, height: 136 },
  small: { x: 295, y: 31, width: 79, height: 120 },
};
const TREE_PIVOT_POINTS = {
  large: { x: 54, y: 122 },
  medium: { x: 46, y: 121 },
  small: { x: 38, y: 106 },
};
const TREE_LOCKED_BASE_HEIGHT = {
  large: 14,
  medium: 14,
  small: 20,
};
const BUSH_TEXTURE_RECTS = {
  extraSmall: { x: 38, y: 198, width: 22, height: 19 },
  small: { x: 98, y: 195, width: 27, height: 25 },
};
const CHEST_TEXTURE_RECTS = {
  closed: { x: 96, y: 30, width: 32, height: 31 },
  open: { x: 96, y: 76, width: 32, height: 49 },
};
const CHEST_SPECS = [
  { id: "top-left", x: 272, y: 226 },
  { id: "north-east", x: 742, y: 168 },
  { id: "mid-east", x: 874, y: 486 },
  { id: "south-center", x: 585, y: 744 },
  { id: "center-west", x: 202, y: 594 },
  { id: "far-east", x: 930, y: 690 },
  { id: "south-west", x: 122, y: 820 },
];

const buildPreviewMapping = (rect) => ({
  localOffset:
    Math.floor(rect.x / TILE_SIZE)
    + Math.floor(rect.y / TILE_SIZE) * TILESET_COLUMNS,
  widthTiles: Math.ceil((rect.x % TILE_SIZE + rect.width) / TILE_SIZE),
  heightTiles: Math.ceil((rect.y % TILE_SIZE + rect.height) / TILE_SIZE),
  offsetX: rect.x % TILE_SIZE,
  offsetY: rect.y % TILE_SIZE,
});

const TREE_PREVIEW_PATTERNS = Object.freeze([
  {
    kind: "large",
    ...buildPreviewMapping(TREE_TEXTURE_RECTS.large),
    widthTiles: 5,
    heightTiles: 5,
  },
  {
    kind: "medium",
    ...buildPreviewMapping(TREE_TEXTURE_RECTS.medium),
    widthTiles: 3,
    heightTiles: 5,
  },
  {
    kind: "small",
    ...buildPreviewMapping(TREE_TEXTURE_RECTS.small),
    widthTiles: 3,
    heightTiles: 5,
  },
]);
const BUSH_PREVIEW_TILES = Object.freeze(
  Object.fromEntries(
    Object.entries(BUSH_TEXTURE_RECTS).map(([kind, rect]) => [
      kind,
      buildPreviewMapping(rect),
    ]),
  ),
);

const readStoryMissionEncounterRuntime = () =>
  typeof window !== "undefined" ? window.__storyMissionEncounterRuntime || null : null;

const getRuntimeRuneStoneImageUrl = () =>
  String(readStoryMissionEncounterRuntime()?.runeStoneImageUrl || "").trim();

const resolveSafeForestSpawn = (
  spawn = null,
  fallback = DEFAULT_FOREST_ENTRY_SPAWN,
  bounds = null,
) => {
  const x = Number(spawn?.x);
  const y = Number(spawn?.y);
  const spawnBounds = bounds || {
    minX: 48,
    maxX: Math.max(48, MAP_PIXEL_WIDTH - 48),
    minY: 48,
    maxY: Math.max(48, MAP_PIXEL_HEIGHT - 48),
  };
  const isValid =
    Number.isFinite(x) &&
    Number.isFinite(y) &&
    x >= spawnBounds.minX &&
    x <= spawnBounds.maxX &&
    y >= spawnBounds.minY &&
    y <= spawnBounds.maxY;

  return isValid ? { x, y } : fallback;
};

const emitStoryDialogue = (script = []) => {
  if (!Array.isArray(script) || !script.length) return;
  window.dispatchEvent(
    new CustomEvent("npc-dialogue", {
      detail: script,
    }),
  );
};

const buildBlockedVoidCaveScript = () => {
  const runtime = readStoryMissionEncounterRuntime();
  const isQuestMasterGate =
    runtime?.missionId === "act-2-mission-3" && Boolean(runtime?.blockVoidCave);

  return [
    {
      speaker: { name: "Infinity Orb" },
      text: isQuestMasterGate
        ? "Quest Master has the Cave route locked until the team is fully cleared to go deeper."
        : "The corruption trail is still active on the forest floor. We should finish this search before heading deeper into the Void Cave.",
    },
  ];
};

const getStoryCreatureTextureKey = (creatureId = "") =>
  `wf-story-creature-${creatureId || "unknown"}`;

const getStoryCreatureAnimationKey = (creatureId = "", animationName = "idle") =>
  `${getStoryCreatureTextureKey(creatureId)}:${animationName}`;

// These forest creature sheets use a shared 5-row layout:
// down, left, right, up, then dead. We keep the row mapping centralized here
// so future creature sheets can stay drop-in compatible.
const STORY_CREATURE_ANIMATION_ROWS = Object.freeze({
  down: 0,
  left: 1,
  right: 2,
  up: 3,
  dead: 4,
});

export default class WhisperingForestScene extends Phaser.Scene {
  constructor() {
    super({ key: "WhisperingForestScene" });
    this.mapSource = whisperingForestSource;
    this.tmj = whisperingForestSource;
    this.tmjLayer = "Collisions";
  }

  init(data = {}) {
    const forestEntrySpawn = this.getForestEntrySpawn();
    this.spawnPoint = resolveSafeForestSpawn(
      data.spawn,
      forestEntrySpawn,
      this.getSafeSpawnBounds(),
    );
    this.returnSpawn = data.returnSpawn || { x: 820, y: 560 };
    this.cityReturnSpawn =
      data.cityReturnSpawn ||
      data.returnSpawn ||
      WHISPERING_FOREST_PORTAL_RETURN_SPAWN;
    this.heroAvatarKey = normalizeAvatarName(data.heroAvatarKey || "io_default");
    this.heroUrl =
      data.heroUrl ||
      SPRITES_MAP[this.heroAvatarKey] ||
      SPRITES_MAP.io_default ||
      null;
    this.heroSpriteConfig = getAvatarSpriteConfig(this.heroAvatarKey);
    this.userData = data.userData || null;
    const { avatarData, textureKey } = getLocalOrbAvatarConfig(this.userData);
    this.localOrbAvatarData = avatarData;
    this.localOrbTextureKey = textureKey;
    this.forestChestMode = data.forestChestMode || "";
    this.openedChests = data.openedChests || {};
    this.storyMissionRuntime = null;
    this.storyRuneStoneTextureLoading = false;
    this.storyRandomChestSpecs = [];
  }

  preload() {
    const runeStoneImageUrl = getRuntimeRuneStoneImageUrl();
    if (runeStoneImageUrl && !this.textures.exists(STORY_RUNE_STONE_TEXTURE_KEY)) {
      this.load.image(STORY_RUNE_STONE_TEXTURE_KEY, runeStoneImageUrl);
    }

    if (this.heroUrl) {
      if (this.textures.exists("hero")) {
        this.textures.remove("hero");
      }
      this.load.spritesheet("hero", this.heroUrl, {
        frameWidth: this.heroSpriteConfig.frameWidth,
        frameHeight: this.heroSpriteConfig.frameHeight,
      });
    }
    preloadAvatars(this, SPRITES_MAP);
    preloadLocalOrbAvatar(this, this.localOrbAvatarData, this.localOrbTextureKey);

    this.load.spritesheet("wfGrass", whisperingGrassPng, {
      frameWidth: 32,
      frameHeight: 32,
    });

    this.load.spritesheet("wfPlant", whisperingPlantPng, {
      frameWidth: 32,
      frameHeight: 32,
    });
    this.load.spritesheet("wfProps", whisperingPropsPng, {
      frameWidth: 32,
      frameHeight: 32,
    });
    this.load.spritesheet("wfForestCave", whisperingForestCavePng, {
      frameWidth: 32,
      frameHeight: 32,
    });
    this.load.image("wfPlantAtlas", whisperingPlantPng);
    this.load.image("wfPropsAtlas", whisperingPropsPng);
    this.load.spritesheet("wfNpcForestScout", forestNpcPng, {
      frameWidth: NPC_FRAME_SIZE,
      frameHeight: NPC_FRAME_SIZE,
    });
    this.load.spritesheet("wfNpcForestScoutIdle", forestNpcIdlePng, {
      frameWidth: NPC_FRAME_SIZE,
      frameHeight: NPC_FRAME_SIZE,
    });
    this.load.spritesheet("wfNpcForestGuide", forestNpc2Png, {
      frameWidth: NPC_FRAME_SIZE,
      frameHeight: NPC_FRAME_SIZE,
    });
    this.load.spritesheet("wfNpcForestGuideIdle", forestNpc2IdlePng, {
      frameWidth: NPC_FRAME_SIZE,
      frameHeight: NPC_FRAME_SIZE,
    });
    this.load.spritesheet("wfNpcForestWanderer", forestNpc3Png, {
      frameWidth: NPC_FRAME_SIZE,
      frameHeight: NPC_FRAME_SIZE,
    });
    this.load.spritesheet("wfNpcForestWandererIdle", forestNpc3IdlePng, {
      frameWidth: NPC_FRAME_SIZE,
      frameHeight: NPC_FRAME_SIZE,
    });
    this.load.spritesheet("wfNpcForestRanger", forestNpc4Png, {
      frameWidth: NPC_FRAME_SIZE,
      frameHeight: NPC_FRAME_SIZE,
    });
    this.load.spritesheet("wfNpcForestRangerIdle", forestNpc4IdlePng, {
      frameWidth: NPC_FRAME_SIZE,
      frameHeight: NPC_FRAME_SIZE,
    });

    STORY_CREATURE_CARDS.forEach((creature) => {
      if (!creature?.worldImage) return;
      const textureKey = getStoryCreatureTextureKey(creature.id);
      if (this.textures.exists(textureKey)) return;
      this.load.spritesheet(textureKey, creature.worldImage, {
        frameWidth: STORY_CREATURE_FRAME_SIZE,
        frameHeight: STORY_CREATURE_FRAME_SIZE,
      });
    });

    AudioManager.loadSFX("door", DOOR_SFX);
  }

  create() {
    applyWorldSceneBackground(this);
    this.anchorRegistry = this.buildAnchorRegistry();
    this.npcPatrolConfigs = this.buildNpcPatrolConfigs();
    this.storyRandomChestSpecs = this.shouldUseStoryRandomPackChests()
      ? this.buildStoryRandomChestSpecs()
      : [];
    this.buildTilesetRegistry();
    this.collisionData = BaseInteriorScene.extractCollisionDataFromTiled(
      this.tmj,
      this.tmjLayer,
    );

    const worldWidth = this.mapSource.width * this.mapSource.tilewidth;
    const worldHeight = this.mapSource.height * this.mapSource.tileheight;

    this.ensureAtlasTextures();
    this.ensureAmbientParticleTextures();
    this.ensureNpcAnimations();
    this.chestSprites = new Map();
    this.chestBodies = new Map();
    this.patrolNpcs = [];
    this.treeSprites = [];
    this.visibleTreeSprites = [];
    this.groundChunks = [];
    this.currentNpc = null;
    this.chatBubble = null;
    this.chatBubbleText = null;
    this.chatBubbleTarget = null;
    this.chatBubbleHideTimer = null;
    this.storyCompanionFollower = null;
    this.storyEncounterSprites = new Map();
    this.storyEncounterLaunchId = "";
    this.storyRuneStone = null;
    this.storyRuneStoneTeleportEffect = null;
    this.storyRuneStoneVisible = false;
    this.storyRuntimeKey = "";
    this.lastBroadcast = {
      x: this.spawnPoint.x,
      y: this.spawnPoint.y,
      direction: 0,
      animState: "idle",
    };
    this.lastFacingDirection = 180;
    this.drawForestBackdrop(worldWidth, worldHeight);
    this.createAmbientForestLife(worldWidth, worldHeight);
    this.createPatrolNpcs();
    this.startTreeWindSway();

    this.cameras.main.setBounds(0, 0, worldWidth, worldHeight);
    this.matter.world.setBounds(0, 0, worldWidth, worldHeight);

    this.collisionData.buildings.forEach((b) => {
      this.addCollisionObjectBody(b);
    });

    this.player = this.matter.add
      .sprite(this.spawnPoint.x, this.spawnPoint.y, "hero", 0)
      .setBody(
        scaleAvatarBodySize(this.heroAvatarKey, { width: 45, height: 65 }),
      )
      .setScale(this.heroSpriteConfig.worldScale || 1)
      .setFixedRotation()
      .setDepth(this.spawnPoint.y + 24);
    this.playerOrbImage = createLocalOrbAvatarImage(
      this,
      this.player,
      this.spawnPoint,
      this.localOrbAvatarData,
      this.localOrbTextureKey,
      { depth: this.spawnPoint.y + 24 },
    );
    this.storyCompanionFollower = createStoryCompanionFollower(this, this.player);

    if (DEBUG_COLLISIONS || this.heroSpriteConfig.showCollisionDebug) {
      this.playerDebugGfx = this.add.graphics().setDepth(5002);
    }

    const isMobile =
      this.sys.game.device.os.android || this.sys.game.device.os.iOS;
    this.cameras.main
      .startFollow(this.player, true, 0.1, 0.1)
      .setZoom(isMobile ? 1.2 : 2.0);
    this.cameras.main.preRender();
    this.updateGroundChunkVisibility();
    this.updateTreeVisibility();
    this.updateVisibleTreeSway(this.time.now);

    createWalkAnimations(this, "hero", this.heroAvatarKey);

    if (this.userData?.id) {
      updatePlayerPresence(this.userData, {
        partyId: this.userData?.partyId,
        host: this.userData?.isHost,
        inGameRoom: false,
        world: STORY_FOREST_WORLD_NAME,
        building: "",
        x: this.player.x,
        y: this.player.y,
        direction: 0,
        animState: "idle",
        device: "desktop",
        roomId: "",
        emote: "",
        level: this.userData?.level,
        name: this.userData?.username,
        avatar: this.userData?.avatar || null,
        orbAvatar: this.userData?.orbAvatar || null,
        online: true,
      });
    }

    window.dispatchEvent(
      new CustomEvent("story-world-location", {
        detail: {
          sceneKey: this.scene.key,
          world: {
            mapId: "whisperingForest",
            building: "",
            spawnId: "whispering-forest-entry",
            x: this.spawnPoint.x,
            y: this.spawnPoint.y,
          },
        },
      }),
    );

    this.currentDoor = null;
    this.currentInteractable = null;
    this.pendingChestOpenId = null;
    this._onChestOpenResult = (event) => {
      const { chestId, ok } = event.detail || {};
      if (!chestId || chestId !== this.pendingChestOpenId) return;
      this.pendingChestOpenId = null;
      if (ok) {
        this.openChest(chestId);
      }
    };
    window.addEventListener(
      "whispering-forest:chest-open-result",
      this._onChestOpenResult,
    );
    this._onStoryWorldEncounterRelease = (event) => {
      const encounterId = String(event?.detail?.encounterId || "");
      if (
        encounterId &&
        this.storyEncounterLaunchId &&
        encounterId !== this.storyEncounterLaunchId
      ) {
        return;
      }
      const entry = this.storyEncounterSprites.get(this.storyEncounterLaunchId);
      if (!entry) {
        this.storyEncounterLaunchId = "";
        return;
      }
      entry.state = "disengaging";
      entry.targetX = entry.homeX;
      entry.targetY = entry.homeY;
    };
    window.addEventListener(
      STORY_WORLD_ENCOUNTER_RELEASE_EVENT,
      this._onStoryWorldEncounterRelease,
    );
    this.matter.world.on("collisionstart", (ev) => {
      ev.pairs.forEach(({ bodyA, bodyB }) => {
        const sensor = bodyA.isSensor ? bodyA : bodyB.isSensor ? bodyB : null;
        const isPlayer =
          bodyA === this.player.body || bodyB === this.player.body;

        if (!sensor || !isPlayer) return;

        if (sensor.label === "exit") {
          this.currentDoor = "exit";
          this.currentInteractable = { type: "exit" };
          this.setInteractPrompt("Whispering Forest Exit");
        } else if (sensor.label === VOID_CAVE_PORTAL_LABEL) {
          this.currentDoor = VOID_CAVE_PORTAL_LABEL;
          this.currentInteractable = { type: "portal", id: VOID_CAVE_PORTAL_LABEL };
          this.setInteractPrompt("Enter Void Cave");
        } else if (sensor.label?.startsWith("chest:")) {
          const chestId = sensor.label.slice(6);
          const chest = this.chestSprites.get(chestId);
          if (!chest || chest.opened) return;
          this.currentInteractable = { type: "chest", id: chestId };
          this.setInteractPrompt("Open Chest");
        } else if (sensor.label === STORY_RUNE_STONE_LABEL) {
          this.currentInteractable = {
            type: "storyItem",
            id: STORY_FOREST_RUNE_STONE_INTERACTABLE_ID,
          };
          this.setInteractPrompt(`Retrieve ${STORY_FOREST_RUNE_STONE_NAME}`);
        } else if (sensor.label?.startsWith("npc:")) {
          const npc = this.patrolNpcs.find((entry) => entry.key === sensor.label.slice(4));
          if (npc) this.setNpcIdle(npc);
        }
      });
    });

    this.matter.world.on("collisionend", (ev) => {
      ev.pairs.forEach(({ bodyA, bodyB }) => {
        const sensor = bodyA.isSensor ? bodyA : bodyB.isSensor ? bodyB : null;
        const isPlayer =
          bodyA === this.player.body || bodyB === this.player.body;

        if (!sensor || !isPlayer) return;

        if (sensor.label === "exit") {
          this.currentDoor = null;
          if (this.currentInteractable?.type === "exit") {
            this.currentInteractable = null;
            this.clearInteractPrompt();
          }
        } else if (sensor.label === VOID_CAVE_PORTAL_LABEL) {
          this.currentDoor = null;
          if (
            this.currentInteractable?.type === "portal"
            && this.currentInteractable.id === VOID_CAVE_PORTAL_LABEL
          ) {
            this.currentInteractable = null;
            this.clearInteractPrompt();
          }
        } else if (
          sensor.label?.startsWith("chest:")
          && this.currentInteractable?.type === "chest"
          && this.currentInteractable.id === sensor.label.slice(6)
        ) {
          this.currentInteractable = null;
          this.clearInteractPrompt();
        } else if (
          sensor.label === STORY_RUNE_STONE_LABEL &&
          this.currentInteractable?.type === "storyItem" &&
          this.currentInteractable?.id === STORY_FOREST_RUNE_STONE_INTERACTABLE_ID
        ) {
          this.currentInteractable = null;
          this.clearInteractPrompt();
        }
      });
    });

    this.joystick = { x: 0, y: 0 };
    this.cursors = this.input.keyboard.createCursorKeys();
    this.keys = this.input.keyboard.addKeys({
      W: Phaser.Input.Keyboard.KeyCodes.W,
      A: Phaser.Input.Keyboard.KeyCodes.A,
      S: Phaser.Input.Keyboard.KeyCodes.S,
      D: Phaser.Input.Keyboard.KeyCodes.D,
    });

    this.spaceKey = this.input.keyboard.addKey(
      Phaser.Input.Keyboard.KeyCodes.SPACE,
    );
    this._onEnter = () => {
      if (
        typeof window !== "undefined" &&
        (
          window.__storySceneInputLocked ||
          window.__storyWorldEncounterPaused
        )
      ) {
        return;
      }

      if (this.currentInteractable?.type === "chest") {
        this.requestOpenChest(this.currentInteractable.id);
        return;
      }

      if (
        this.currentInteractable?.type === "storyItem" &&
        this.currentInteractable?.id === STORY_FOREST_RUNE_STONE_INTERACTABLE_ID
      ) {
        window.dispatchEvent(
          new CustomEvent("story-item-interacted", {
            detail: {
              missionId: STORY_RUNE_STONE_MISSION_ID,
              interactableId: STORY_FOREST_RUNE_STONE_INTERACTABLE_ID,
              sceneKey: this.scene.key,
            },
          }),
        );
        return;
      }

      if (this.currentNpc && !this.currentInteractable && !this.currentDoor) {
        this.showNpcChatBubble(this.currentNpc);
        return;
      }

      if (this.currentDoor === VOID_CAVE_PORTAL_LABEL) {
        if (this.shouldBlockVoidCavePortal()) {
          emitStoryDialogue(buildBlockedVoidCaveScript());
          return;
        }
        this.enterVoidCaveScene();
        return;
      }

      if (this.currentDoor !== "exit") return;

      AudioManager.playSFX("door");
      this.cameras.main.fadeOut(300, 0, 0, 0);
      this.cameras.main.once("camerafadeoutcomplete", () => {
        clearWhisperingForestStoryChestVisit();
        this.scene.stop(this.scene.key);
        this.scene.start("VoidCity2Scene", {
          spawn: WHISPERING_FOREST_PORTAL_RETURN_SPAWN,
          returnSpawn: WHISPERING_FOREST_PORTAL_RETURN_SPAWN,
          heroUrl: this.heroUrl,
          heroAvatarKey: this.heroAvatarKey,
          userData: this.userData,
        });
      });
    };
    this.spaceKey.on("down", this._onEnter);
    window.addEventListener("press-enter", this._onEnter);
    this._onStoryRuneStoneTeleport = (event) => {
      const targetSceneKey = String(event?.detail?.sceneKey || "");
      if (targetSceneKey && targetSceneKey !== this.scene.key) return;
      if (this.userData?.activeGameMode !== "story") return;
      this.playStoryRuneStoneTeleportEffect(
        String(event?.detail?.effectId || ""),
      );
    };
    window.addEventListener(
      "story-rune-stone-teleport",
      this._onStoryRuneStoneTeleport,
    );

    this.events.once("shutdown", () => {
      this.spaceKey?.off?.("down", this._onEnter);
      window.removeEventListener("press-enter", this._onEnter);
      window.removeEventListener(
        "story-rune-stone-teleport",
        this._onStoryRuneStoneTeleport,
      );
      window.removeEventListener(
        "whispering-forest:chest-open-result",
        this._onChestOpenResult,
      );
      window.removeEventListener(
        STORY_WORLD_ENCOUNTER_RELEASE_EVENT,
        this._onStoryWorldEncounterRelease,
      );
      this.chatBubbleHideTimer?.remove(false);
      destroyStoryCompanionFollower(this.storyCompanionFollower);
      this.destroyStoryEncounterSprites();
      this.destroyStoryRuneStoneTeleportEffect();
      this.destroyStoryRuneStone();
      window.dispatchEvent(
        new CustomEvent("interact-proximity", { detail: false }),
      );
      window.dispatchEvent(new CustomEvent("door-name", { detail: null }));
      this.currentInteractable = null;
    });

    this.cameras.main.fadeIn(300, 0, 0, 0);
  }

  enterVoidCaveScene() {
    this.currentDoor = null;
    this.currentInteractable = null;
    this.clearInteractPrompt();
    const storyLaunchTarget =
      typeof window !== "undefined" ? window.__storyLaunchTarget : null;
    const shouldTriggerStoryVoidCaveEntry =
      this.userData?.activeGameMode === "story" &&
      storyLaunchTarget?.kind === "world" &&
      String(storyLaunchTarget?.triggerSceneKey || "") === this.scene.key &&
      String(storyLaunchTarget?.triggerZoneId || "") === "void-cave-entry";

    if (shouldTriggerStoryVoidCaveEntry) {
      window.dispatchEvent(
        new CustomEvent("story-world-trigger", {
          detail: {
            sceneKey: this.scene.key,
            triggerZoneId: "void-cave-entry",
          },
        }),
      );
    }
    AudioManager.playSFX("door");
    this.cameras.main.fadeOut(300, 0, 0, 0);
    this.cameras.main.once("camerafadeoutcomplete", () => {
      clearWhisperingForestStoryChestVisit();
      this.scene.stop(this.scene.key);
      this.scene.start("VoidCaveScene", {
        spawn: VOID_CAVE_ENTRY_SPAWN,
        returnSpawn: WHISPERING_FOREST_VOID_CAVE_RETURN_SPAWN,
        cityReturnSpawn: WHISPERING_FOREST_PORTAL_RETURN_SPAWN,
        returnSceneKey: "WhisperingForestScene",
        returnDoorLabel: "Return To Whispering Forest",
        heroUrl: this.heroUrl,
        heroAvatarKey: this.heroAvatarKey,
        userData: this.userData,
      });
    });
  }

  shouldBlockVoidCavePortal() {
    const runtime = readStoryMissionEncounterRuntime();
    return Boolean(
      this.userData?.activeGameMode === "story" &&
      runtime &&
      (
        runtime.blockVoidCave ||
        runtime.missionId === STORY_FOREST_PURGE_MISSION_ID ||
        runtime.missionId === STORY_RUNE_STONE_MISSION_ID
      ),
    );
  }

  syncStoryMissionRuntime() {
    const runtime = readStoryMissionEncounterRuntime();
    const nextRuntimeKey = JSON.stringify({
      missionId: runtime?.missionId || "",
      sceneKey: runtime?.sceneKey || "",
      encounterSetId: runtime?.encounterSetId || "",
      defeatedEncounterIds: runtime?.defeatedEncounterIds || [],
      runeStoneRevealed: Boolean(runtime?.runeStoneRevealed),
      activeInteractableId: runtime?.activeInteractableId || null,
      runeStoneImageUrl: runtime?.runeStoneImageUrl || "",
      encounterIds: (runtime?.encounters || []).map((encounter) => encounter?.encounterId),
    });

    if (this.storyRuntimeKey === nextRuntimeKey) {
      return runtime;
    }

    this.storyRuntimeKey = nextRuntimeKey;
    this.storyMissionRuntime = runtime;

    if (
      runtime?.encounterMode === STORY_FOREST_ENCOUNTER_MODE &&
      String(runtime?.sceneKey || "WhisperingForestScene") === "WhisperingForestScene"
    ) {
      this.ensureStoryEncounterSprites(runtime);
      this.syncStoryEncounterVisibility(runtime);
      if (runtime?.runeStoneRevealed) {
        this.ensureStoryRuneStone();
      } else {
        this.destroyStoryRuneStone();
      }
      return runtime;
    }

    this.destroyStoryEncounterSprites();

    if (
      runtime?.missionId === STORY_RUNE_STONE_MISSION_ID &&
      runtime?.activeInteractableId === STORY_FOREST_RUNE_STONE_INTERACTABLE_ID
    ) {
      this.ensureStoryRuneStone();
    } else {
      this.destroyStoryRuneStone();
      if (
        this.currentInteractable?.type === "storyItem" &&
        this.currentInteractable?.id === STORY_FOREST_RUNE_STONE_INTERACTABLE_ID
      ) {
        this.currentInteractable = null;
        this.clearInteractPrompt();
      }
    }

    return runtime;
  }

  ensureStoryEncounterSprites(runtime = null) {
    const encounters =
      Array.isArray(runtime?.encounters) && runtime.encounters.length
        ? runtime.encounters
        : STORY_FOREST_ENCOUNTERS;
    const nextEncounterIds = new Set(
      encounters.map((encounter) => String(encounter?.encounterId || "")),
    );

    this.storyEncounterSprites.forEach((entry, encounterId) => {
      if (nextEncounterIds.has(String(encounterId || ""))) return;
      entry?.sprite?.destroy?.();
      entry?.shadow?.destroy?.();
      entry?.badgeBg?.destroy?.();
      entry?.badgeText?.destroy?.();
      this.storyEncounterSprites.delete(encounterId);
    });

    encounters.forEach((encounter) => {
      const creature = getStoryCreatureCard(encounter.leadCreatureId);
      const textureKey = getStoryCreatureTextureKey(
        creature?.id || encounter.encounterId,
      );
      if (this.storyEncounterSprites.has(encounter.encounterId)) return;
      if (!creature || !this.textures.exists(textureKey)) return;
      this.ensureStoryCreatureAnimations(creature.id);

      const sprite = this.add
        .sprite(encounter.home.x, encounter.home.y, textureKey, 0)
        .setScale(STORY_ENCOUNTER_SCALE)
        .setDepth(encounter.home.y + 8);
      const shadow = this.add.ellipse(
        encounter.home.x,
        encounter.home.y + 14,
        34,
        12,
        0x102215,
        STORY_ENCOUNTER_SHADOW_ALPHA,
      ).setDepth(encounter.home.y + 7);

      const badgeBg = this.add.graphics().setDepth(encounter.home.y + 18);
      const badgeText = this.add.text(0, 0, `x${encounter.creatureIds.length}`, {
        font: "bold 14px Arial",
        fill: "#f7fff1",
        stroke: "#0e2012",
        strokeThickness: 4,
      })
        .setOrigin(0.5)
        .setDepth(encounter.home.y + 19);

      this.storyEncounterSprites.set(encounter.encounterId, {
        encounterId: encounter.encounterId,
        encounter,
        creature,
        sprite,
        shadow,
        badgeBg,
        badgeText,
        homeX: encounter.home.x,
        homeY: encounter.home.y,
        x: encounter.home.x,
        y: encounter.home.y,
        targetX: encounter.home.x,
        targetY: encounter.home.y,
        state: "idle",
        waitUntil: 0,
        hidden: false,
        lastDirection: "down",
      });
    });
  }

  ensureStoryCreatureAnimations(creatureId = "") {
    const normalizedCreatureId = String(creatureId || "").trim().toLowerCase();
    if (!normalizedCreatureId) return;

    const textureKey = getStoryCreatureTextureKey(normalizedCreatureId);
    if (!this.textures.exists(textureKey)) return;

    Object.entries(STORY_CREATURE_ANIMATION_ROWS).forEach(([direction, rowIndex]) => {
      const animationKey = getStoryCreatureAnimationKey(
        normalizedCreatureId,
        direction,
      );
      if (this.anims.exists(animationKey)) return;

      const start = rowIndex * STORY_CREATURE_FRAMES_PER_ROW;
      const end = start + STORY_CREATURE_FRAMES_PER_ROW - 1;
      this.anims.create({
        key: animationKey,
        frames: this.anims.generateFrameNumbers(textureKey, {
          start,
          end,
        }),
        frameRate: STORY_CREATURE_ANIMATION_FRAME_RATE,
        repeat: direction === "dead" ? 0 : -1,
      });
    });
  }

  setStoryEncounterAnimation(entry, direction = "down", moving = false) {
    if (!entry?.sprite || !entry?.creature?.id) return;

    const nextDirection = Object.prototype.hasOwnProperty.call(
      STORY_CREATURE_ANIMATION_ROWS,
      direction,
    )
      ? direction
      : "down";
    entry.lastDirection = nextDirection;

    if (moving) {
      const animationKey = getStoryCreatureAnimationKey(
        entry.creature.id,
        nextDirection,
      );
      if (entry.sprite.anims.currentAnim?.key !== animationKey) {
        entry.sprite.play(animationKey, true);
      }
      return;
    }

    entry.sprite.anims.stop();
    const idleFrame =
      Number(STORY_CREATURE_ANIMATION_ROWS[nextDirection] || 0) *
      STORY_CREATURE_FRAMES_PER_ROW;
    entry.sprite.setFrame(idleFrame);
  }

  destroyStoryEncounterSprites() {
    this.storyEncounterLaunchId = "";
    this.storyEncounterSprites.forEach((entry) => {
      entry?.sprite?.destroy?.();
      entry?.shadow?.destroy?.();
      entry?.badgeBg?.destroy?.();
      entry?.badgeText?.destroy?.();
    });
    this.storyEncounterSprites.clear();
  }

  syncStoryEncounterVisibility(runtime = null) {
    const defeatedEncounterIds = new Set(runtime?.defeatedEncounterIds || []);

    this.storyEncounterSprites.forEach((entry) => {
      const hidden = defeatedEncounterIds.has(entry.encounterId);
      entry.hidden = hidden;
      entry.sprite.setVisible(!hidden);
      entry.shadow.setVisible(!hidden);
      entry.badgeText.setVisible(!hidden);
      entry.badgeBg.setVisible(!hidden);

      if (hidden && this.storyEncounterLaunchId === entry.encounterId) {
        this.storyEncounterLaunchId = "";
      }

      if (hidden) {
        entry.sprite.anims?.stop?.();
      } else {
        this.setStoryEncounterAnimation(entry, entry.lastDirection || "down", false);
      }
    });
  }

  createRandomEncounterHomeTarget(entry) {
    const halfWidth = Number(entry?.encounter?.homeBounds?.width || 0) / 2;
    const halfHeight = Number(entry?.encounter?.homeBounds?.height || 0) / 2;

    return {
      x: Phaser.Math.Clamp(
        entry.homeX + Phaser.Math.Between(-halfWidth, halfWidth),
        36,
        this.cameras.main.getBounds().width - 36,
      ),
      y: Phaser.Math.Clamp(
        entry.homeY + Phaser.Math.Between(-halfHeight, halfHeight),
        36,
        this.cameras.main.getBounds().height - 36,
      ),
    };
  }

  updateStoryEncounterSprites() {
    const runtime = this.storyMissionRuntime;
    let encountersPaused =
      typeof window !== "undefined" && Boolean(window.__storyWorldEncounterPaused);
    if (
      encountersPaused ||
      !runtime ||
      runtime?.encounterMode !== STORY_FOREST_ENCOUNTER_MODE ||
      String(runtime?.sceneKey || "WhisperingForestScene") !== "WhisperingForestScene"
    ) {
      return;
    }

    this.storyEncounterSprites.forEach((entry) => {
      if (entry.hidden || encountersPaused) return;

      const { encounter } = entry;
      const dxToPlayer = this.player.x - entry.x;
      const dyToPlayer = this.player.y - entry.y;
      const distanceToPlayer = Math.hypot(dxToPlayer, dyToPlayer);

      if (
        this.storyEncounterLaunchId !== entry.encounterId &&
        distanceToPlayer <= Number(encounter.touchRange || 34)
      ) {
        const encounterDetail = {
          missionId: runtime?.missionId || STORY_FOREST_PURGE_MISSION_ID,
          encounterMode: runtime?.encounterMode || STORY_FOREST_ENCOUNTER_MODE,
          encounterSetId: runtime?.encounterSetId || null,
          encounterId: entry.encounterId,
          sceneKey: this.scene.key,
          world: runtime?.world || null,
          playerWorld: {
            ...(runtime?.world || {}),
            mapId: "whisperingForest",
            building: "",
            x: this.player.x,
            y: this.player.y,
            facingDirection: this.lastFacingDirection,
          },
        };
        this.storyEncounterLaunchId = entry.encounterId;
        if (typeof window !== "undefined") {
          window.__storyWorldEncounterPaused = true;
          window.__pendingStoryWorldEncounter = encounterDetail;
        }
        encountersPaused = true;
        window.dispatchEvent(
          new CustomEvent("story-world-encounter", {
            detail: encounterDetail,
          }),
        );
        return;
      }

      if (
        entry.state !== "chasing" &&
        entry.state !== "disengaging" &&
        distanceToPlayer <= Number(encounter.aggroRange || 128)
      ) {
        entry.state = "chasing";
      } else if (
        entry.state === "chasing" &&
        distanceToPlayer >= Number(encounter.giveUpRange || 240)
      ) {
        entry.state = "returning";
      }

      if (entry.state === "idle" && this.time.now >= entry.waitUntil) {
        const nextTarget = this.createRandomEncounterHomeTarget(entry);
        entry.targetX = nextTarget.x;
        entry.targetY = nextTarget.y;
        entry.state = "roaming";
      }

      let targetX = entry.targetX;
      let targetY = entry.targetY;
      let speed = Number(encounter.roamSpeed || 0.35);

      if (entry.state === "chasing") {
        targetX = this.player.x;
        targetY = this.player.y;
        speed = Number(encounter.chaseSpeed || 1.3);
      } else if (entry.state === "returning" || entry.state === "disengaging") {
        targetX = entry.homeX;
        targetY = entry.homeY;
        speed = Number(encounter.chaseSpeed || 1.3) * 0.82;
      }

      const dx = targetX - entry.x;
      const dy = targetY - entry.y;
      const distance = Math.hypot(dx, dy);
      let isMoving = false;
      let nextDirection = entry.lastDirection || "down";

      if (distance <= Math.max(1, speed + 0.25)) {
        entry.x = targetX;
        entry.y = targetY;
        if (entry.state === "roaming") {
          entry.state = "idle";
          entry.waitUntil = this.time.now + STORY_ENCOUNTER_IDLE_WAIT_MS;
        } else if (entry.state === "returning" || entry.state === "disengaging") {
          if (
            entry.state === "disengaging" &&
            this.storyEncounterLaunchId === entry.encounterId
          ) {
            this.storyEncounterLaunchId = "";
          }
          entry.state = "idle";
          entry.waitUntil = this.time.now + 600;
        }
      } else if (distance > 0) {
        entry.x += (dx / distance) * speed;
        entry.y += (dy / distance) * speed;
        isMoving = true;
        if (Math.abs(dy) > Math.abs(dx)) {
          nextDirection = dy < 0 ? "up" : "down";
        } else {
          nextDirection = dx < 0 ? "left" : "right";
        }
      }

      this.setStoryEncounterAnimation(entry, nextDirection, isMoving);
      entry.sprite.setPosition(entry.x, entry.y).setDepth(entry.y + 10);
      entry.shadow.setPosition(entry.x, entry.y + 14).setDepth(entry.y + 9);
      entry.badgeText
        .setPosition(entry.x, entry.y - STORY_ENCOUNTER_LABEL_OFFSET_Y)
        .setDepth(entry.y + 20);
      entry.badgeBg.clear();
      entry.badgeBg
        .fillStyle(0x102215, 0.74)
        .fillRoundedRect(entry.x - 16, entry.y - 48, 32, 20, 8)
        .lineStyle(2, 0xeaf6db, 0.9)
        .strokeRoundedRect(entry.x - 16, entry.y - 48, 32, 20, 8)
        .setDepth(entry.y + 19);
    });
  }

  ensureStoryRuneStone() {
    if (this.storyRuneStone) {
      this.storyRuneStoneVisible = true;
      this.storyRuneStone.container.setVisible(true);
      this.ensureStoryRuneStoneCardImage();
      return;
    }

    const { x, y } = this.getStoryRuneStonePosition();
    const container = this.add
      .container(x, y)
      .setDepth(y + 24)
      .setScale(STORY_INFINITY_ITEM_WORLD_SCALE);
    const glow = this.add.graphics();
    glow.fillStyle(0x63d5ff, 0.18);
    glow.fillCircle(0, 18, 44);
    glow.fillStyle(0xe7fbff, 0.48);
    glow.fillCircle(0, 2, 18);
    glow.lineStyle(3, 0xf6fdff, 0.95);
    glow.strokeCircle(0, 2, 18);

    const pedestal = this.add.graphics();
    pedestal.fillStyle(0x2b3440, 0.96);
    pedestal.fillRoundedRect(-20, 14, 40, 12, 4);
    pedestal.fillStyle(0x5a6b82, 0.96);
    pedestal.fillRoundedRect(-14, 7, 28, 10, 4);

    const placeholderStone = this.add.graphics();
    placeholderStone.fillStyle(0x91f3ff, 0.95);
    placeholderStone.beginPath();
    placeholderStone.moveTo(0, -18);
    placeholderStone.lineTo(10, -8);
    placeholderStone.lineTo(7, 6);
    placeholderStone.lineTo(-7, 6);
    placeholderStone.lineTo(-10, -8);
    placeholderStone.closePath();
    placeholderStone.fillPath();
    placeholderStone.lineStyle(3, 0xeefcff, 0.9);
    placeholderStone.strokePath();

    const pulse = this.add.graphics();
    pulse.lineStyle(2, 0xc8f6ff, 0.75);
    pulse.strokeCircle(0, 2, 26);
    this.tweens.add({
      targets: pulse,
      alpha: { from: 0.75, to: 0.28 },
      scaleX: { from: 1, to: 1.12 },
      scaleY: { from: 1, to: 1.12 },
      duration: 1100,
      yoyo: true,
      repeat: -1,
      ease: "Sine.InOut",
    });

    container.add([glow, pedestal, pulse, placeholderStone]);

    const sensor = this.matter.add.rectangle(x, y + 6, 56, 56, {
      isStatic: true,
      isSensor: true,
      label: STORY_RUNE_STONE_LABEL,
    });

    this.storyRuneStone = {
      container,
      placeholderStone,
      sensor,
      x,
      y,
    };
    this.storyRuneStoneVisible = true;
    this.ensureStoryRuneStoneCardImage();
  }

  applyStoryRuneStoneCardTexture() {
    if (
      !this.storyRuneStone ||
      !this.textures.exists(STORY_RUNE_STONE_TEXTURE_KEY)
    ) {
      return;
    }

    this.storyRuneStone.cardImage?.destroy?.();
    // Story infinity-card pickups render as square world icons so any future
    // card-based artifacts keep the same compact footprint in the scene.
    const cardImage = this.add
      .image(0, -8, STORY_RUNE_STONE_TEXTURE_KEY)
      .setOrigin(0.5);
    const sourceImage = this.textures
      .get(STORY_RUNE_STONE_TEXTURE_KEY)
      ?.getSourceImage?.();
    const sourceWidth = Number(sourceImage?.width || 0);
    const sourceHeight = Number(sourceImage?.height || 0);
    const cropSize = Math.min(sourceWidth, sourceHeight);
    if (cropSize > 0) {
      const cropX = Math.max(0, (sourceWidth - cropSize) / 2);
      const cropY = Math.max(0, (sourceHeight - cropSize) / 2);
      cardImage.setCrop(cropX, cropY, cropSize, cropSize);
    }
    cardImage.setDisplaySize(
      STORY_INFINITY_ITEM_ICON_SIZE,
      STORY_INFINITY_ITEM_ICON_SIZE,
    );
    this.storyRuneStone.placeholderStone?.setVisible(false);
    this.storyRuneStone.container.add(cardImage);
    this.storyRuneStone.cardImage = cardImage;
  }

  ensureStoryRuneStoneCardImage() {
    if (!this.storyRuneStone) return;
    if (this.textures.exists(STORY_RUNE_STONE_TEXTURE_KEY)) {
      this.applyStoryRuneStoneCardTexture();
      return;
    }

    const imageUrl = getRuntimeRuneStoneImageUrl();
    if (!imageUrl || this.storyRuneStoneTextureLoading) return;

    this.storyRuneStoneTextureLoading = true;
    this.load.image(STORY_RUNE_STONE_TEXTURE_KEY, imageUrl);
    this.load.once(`filecomplete-image-${STORY_RUNE_STONE_TEXTURE_KEY}`, () => {
      this.storyRuneStoneTextureLoading = false;
      this.applyStoryRuneStoneCardTexture();
    });
    this.load.once("loaderror", (file) => {
      if (file?.key === STORY_RUNE_STONE_TEXTURE_KEY) {
        this.storyRuneStoneTextureLoading = false;
      }
    });
    this.load.start();
  }

  syncStoryRuneStoneInteraction() {
    const runtime = this.storyMissionRuntime || readStoryMissionEncounterRuntime();
    const runeStoneActive =
      runtime?.missionId === STORY_RUNE_STONE_MISSION_ID &&
      runtime?.activeInteractableId === STORY_FOREST_RUNE_STONE_INTERACTABLE_ID &&
      this.storyRuneStoneVisible &&
      this.storyRuneStone &&
      this.player;

    if (!runeStoneActive) {
      if (
        this.currentInteractable?.type === "storyItem" &&
        this.currentInteractable?.id === STORY_FOREST_RUNE_STONE_INTERACTABLE_ID
      ) {
        this.currentInteractable = null;
        if (!this.currentDoor) {
          this.clearInteractPrompt();
        }
      }
      return;
    }

    const distance = Phaser.Math.Distance.Between(
      this.player.x,
      this.player.y,
      this.storyRuneStone.x,
      this.storyRuneStone.y + 6,
    );
    const inRange = distance <= STORY_RUNE_STONE_INTERACT_RANGE;

    if (inRange) {
      if (
        this.currentInteractable?.type !== "storyItem" ||
        this.currentInteractable?.id !== STORY_FOREST_RUNE_STONE_INTERACTABLE_ID
      ) {
        this.currentInteractable = {
          type: "storyItem",
          id: STORY_FOREST_RUNE_STONE_INTERACTABLE_ID,
        };
        this.setInteractPrompt(`Retrieve ${STORY_FOREST_RUNE_STONE_NAME}`);
      }
      return;
    }

    if (
      this.currentInteractable?.type === "storyItem" &&
      this.currentInteractable?.id === STORY_FOREST_RUNE_STONE_INTERACTABLE_ID
    ) {
      this.currentInteractable = null;
      if (!this.currentDoor) {
        this.clearInteractPrompt();
      }
    }
  }

  destroyStoryRuneStone() {
    if (!this.storyRuneStone) return;

    this.storyRuneStone.container?.destroy?.();
    if (this.storyRuneStone.sensor) {
      // Scene shutdown can null out Matter before late cleanup callbacks run.
      // Guard the body removal so returning to Void City never crashes teardown.
      this.matter?.world?.remove?.(this.storyRuneStone.sensor);
    }
    this.storyRuneStone = null;
    this.storyRuneStoneVisible = false;
  }

  emitStoryRuneStoneTeleportComplete(effectId = "") {
    if (typeof window === "undefined" || !effectId) return;

    window.dispatchEvent(
      new CustomEvent("story-rune-stone-teleport-complete", {
        detail: {
          effectId,
          sceneKey: this.scene.key,
        },
      }),
    );
  }

  getStoryRuneStoneTeleportAnchor() {
    const position = this.player?.body?.position;
    if (
      position &&
      Number.isFinite(position.x) &&
      Number.isFinite(position.y)
    ) {
      return {
        x: position.x,
        y: position.y,
      };
    }

    return null;
  }

  playStoryRuneStoneTeleportEffect(effectId = "") {
    if (!effectId) return;

    this.destroyStoryRuneStoneTeleportEffect();

    const anchor = this.getStoryRuneStoneTeleportAnchor();
    if (!anchor) {
      this.emitStoryRuneStoneTeleportComplete(effectId);
      return;
    }

    this.storyRuneStoneTeleportEffect = createStoryRuneStoneTeleportEffect(this, {
      x: anchor.x,
      y: anchor.y + STORY_RUNE_STONE_TELEPORT_Y_OFFSET,
      depth: (this.player?.depth || anchor.y + 24) + 120,
      target: this.player,
      onComplete: () => {
        this.storyRuneStoneTeleportEffect = null;
        this.emitStoryRuneStoneTeleportComplete(effectId);
      },
    });
  }

  syncStoryRuneStoneTeleportEffect() {
    const anchor = this.getStoryRuneStoneTeleportAnchor();
    if (!anchor || !this.storyRuneStoneTeleportEffect) return;

    syncStoryRuneStoneTeleportEffect(this.storyRuneStoneTeleportEffect, {
      x: anchor.x,
      y: anchor.y + STORY_RUNE_STONE_TELEPORT_Y_OFFSET,
      depth: (this.player?.depth || anchor.y + 24) + 120,
    });
  }

  destroyStoryRuneStoneTeleportEffect() {
    destroyStoryRuneStoneTeleportEffect(this.storyRuneStoneTeleportEffect);
    this.storyRuneStoneTeleportEffect = null;
  }

  buildAnchorRegistry() {
    const anchorLayer = this.getObjectLayerByName("Anchors");
    const registry = new Map();
    anchorLayer?.objects?.forEach((object) => {
      if (object?.name) {
        registry.set(object.name, object);
      }
    });
    return registry;
  }

  getAnchorByName(name, fallback = null) {
    if (this.anchorRegistry?.has(name)) {
      return this.anchorRegistry.get(name);
    }

    const anchorLayer = this.getObjectLayerByName("Anchors");
    return anchorLayer?.objects?.find((object) => object.name === name) || fallback;
  }

  getSafeSpawnBounds() {
    return {
      minX: 48,
      maxX: Math.max(48, this.mapSource.width * this.mapSource.tilewidth - 48),
      minY: 48,
      maxY: Math.max(48, this.mapSource.height * this.mapSource.tileheight - 48),
    };
  }

  getForestEntrySpawn() {
    const anchor = this.getAnchorByName("forestEntrySpawn");
    if (!anchor) {
      return DEFAULT_FOREST_ENTRY_SPAWN;
    }

    return {
      x: Number(anchor.x || 0),
      y: Number(anchor.y || 0),
    };
  }

  getVoidCavePortalRect() {
    const anchor = this.getAnchorByName("voidCavePortal");
    if (!anchor) {
      return DEFAULT_VOID_CAVE_PORTAL;
    }

    return {
      x: Number(anchor.x || 0),
      y: Number(anchor.y || 0),
      width: Number(anchor.width || DEFAULT_VOID_CAVE_PORTAL.width),
      height: Number(anchor.height || DEFAULT_VOID_CAVE_PORTAL.height),
    };
  }

  getExitAnchorRect() {
    const anchor = this.getAnchorByName("exit");
    if (!anchor) {
      return {
        x: 448,
        y: 944,
        width: 128,
        height: 32,
      };
    }

    return {
      x: Number(anchor.x || 0),
      y: Number(anchor.y || 0),
      width: Number(anchor.width || 0),
      height: Number(anchor.height || 0),
    };
  }

  getStoryRuneStonePosition() {
    const anchor = this.getAnchorByName("storyRuneStone");
    if (!anchor) {
      return DEFAULT_STORY_RUNE_STONE_POSITION;
    }

    return {
      x: Number(anchor.x || 0),
      y: Number(anchor.y || 0),
    };
  }

  shouldUseStoryRandomPackChests() {
    return isWhisperingForestStoryPackChestMode(this.forestChestMode);
  }

  buildStoryRandomChestSpecs() {
    return getOrCreateWhisperingForestStoryChestVisit({
      entrySpawn: this.getForestEntrySpawn(),
      exitRect: this.getExitAnchorRect(),
      voidCavePortalRect: this.getVoidCavePortalRect(),
      runeStonePosition: this.getStoryRuneStonePosition(),
      npcStartPoints: this.npcPatrolConfigs.map((npc) => npc.start),
      isPointBlocked: isStoryForestPointBlocked,
    });
  }

  getChestSpecs() {
    return this.shouldUseStoryRandomPackChests()
      ? this.storyRandomChestSpecs
      : CHEST_SPECS;
  }

  buildNpcPatrolConfigs() {
    const patrolLayer = this.getObjectLayerByName("NpcPatrols");
    const sourceObjects = new Map(
      (patrolLayer?.objects || [])
        .filter((object) => object?.name)
        .map((object) => [object.name, object]),
    );

    return NPC_PATROL_METADATA.map((config) => {
      const sourceObject = sourceObjects.get(config.key);
      if (!sourceObject) {
        return config;
      }

      const start = {
        x: Number(sourceObject.x || config.start.x),
        y: Number(sourceObject.y || config.start.y),
      };
      const sourcePath = Array.isArray(sourceObject.polyline) && sourceObject.polyline.length > 0
        ? [
          start,
          ...sourceObject.polyline.map((point) => ({
            x: start.x + Number(point.x || 0),
            y: start.y + Number(point.y || 0),
          })),
        ]
        : config.path;

      return {
        ...config,
        start,
        path: sourcePath,
      };
    });
  }

  buildTilesetRegistry() {
    const textureKeyByBasename = {
      "whispering-forest-grass.tsx": "wfGrass",
      "whispering-forest-plant.tsx": "wfPlant",
      "whispering-forest-props.tsx": "wfProps",
      "grass1.tsx": "wfGrass",
      "forest_cave.tsx": "wfForestCave",
    };

    this.tilesetRegistry = [...(this.mapSource?.tilesets || [])]
      .map((tileset) => ({
        ...tileset,
        textureKey:
          textureKeyByBasename[tileset.basename || tileset.source?.split("/").pop()],
      }))
      .filter((tileset) => tileset.textureKey)
      .sort((a, b) => a.firstgid - b.firstgid);
  }

  resolveTilesetForGid(gid) {
    if (!gid) return null;

    let found = null;
    this.tilesetRegistry.forEach((tileset) => {
      if (tileset.firstgid <= gid) {
        found = tileset;
      }
    });

    if (!found) return null;
    return {
      ...found,
      frame: gid - found.firstgid,
    };
  }

  getLayerByName(name) {
    return this.mapSource?.layers?.find(
      (layer) => layer.type === "tilelayer" && layer.name === name,
    );
  }

  getObjectLayerByName(name) {
    return this.mapSource?.layers?.find(
      (layer) => layer.type === "objectgroup" && layer.name === name,
    );
  }

  getLatestTilesetFirstgid(basename) {
    return (this.mapSource?.tilesets || [])
      .filter((tileset) => tileset.basename === basename)
      .map((tileset) => tileset.firstgid)
      .sort((a, b) => a - b)
      .at(-1)
      || null;
  }

  getOrderedStaticSourceLayers() {
    return (this.mapSource?.layers || [])
      .filter(
        (layer) =>
          layer.type === "tilelayer" && !PREVIEW_LAYER_NAMES.has(layer.name),
      );
  }

  drawStaticLayersFromSource() {
    this.groundChunks = [];
    this.getOrderedStaticSourceLayers().forEach((layer, index) => {
      this.drawStaticLayerChunksFromSource(layer, 4 + index);
    });
  }

  drawStaticLayerChunksFromSource(layer, depth) {
    if (!layer?.data?.length) return;

    for (let chunkTileY = 0; chunkTileY < layer.height; chunkTileY += GROUND_CHUNK_TILES) {
      for (let chunkTileX = 0; chunkTileX < layer.width; chunkTileX += GROUND_CHUNK_TILES) {
        const chunkTilesWide = Math.min(GROUND_CHUNK_TILES, layer.width - chunkTileX);
        const chunkTilesHigh = Math.min(GROUND_CHUNK_TILES, layer.height - chunkTileY);
        const chunkPixelWidth = chunkTilesWide * TILE_SIZE;
        const chunkPixelHeight = chunkTilesHigh * TILE_SIZE;
        const groundRT = this.make.renderTexture({
          width: chunkPixelWidth,
          height: chunkPixelHeight,
          add: false,
        });

        for (let localTileY = 0; localTileY < chunkTilesHigh; localTileY += 1) {
          for (let localTileX = 0; localTileX < chunkTilesWide; localTileX += 1) {
            const tileX = chunkTileX + localTileX;
            const tileY = chunkTileY + localTileY;
            const gid = layer.data[tileY * layer.width + tileX];
            if (!gid) continue;

            const resolved = this.resolveTilesetForGid(gid);
            if (!resolved) continue;

            groundRT.drawFrame(
              resolved.textureKey,
              resolved.frame,
              localTileX * TILE_SIZE,
              localTileY * TILE_SIZE,
            );
          }
        }

        const textureKey = `wf-${layer.name}-${chunkTileX}-${chunkTileY}`;
        if (this.textures.exists(textureKey)) {
          this.textures.remove(textureKey);
        }
        groundRT.saveTexture(textureKey);

        const worldX = chunkTileX * TILE_SIZE;
        const worldY = chunkTileY * TILE_SIZE;
        const image = this.add
          .image(worldX, worldY, textureKey)
          .setOrigin(0)
          .setDepth(depth);

        this.groundChunks.push({
          image,
          layerName: layer.name,
          x: worldX,
          y: worldY,
          width: chunkPixelWidth,
          height: chunkPixelHeight,
        });
      }
    }
  }

  updateGroundChunkVisibility() {
    if (!this.groundChunks.length || !this.cameras?.main) return;

    const view = this.cameras.main.worldView;
    const minX = view.x - GROUND_CHUNK_PADDING;
    const maxX = view.right + GROUND_CHUNK_PADDING;
    const minY = view.y - GROUND_CHUNK_PADDING;
    const maxY = view.bottom + GROUND_CHUNK_PADDING;

    this.groundChunks.forEach((chunk) => {
      const visible = !(
        chunk.x + chunk.width < minX
        || chunk.x > maxX
        || chunk.y + chunk.height < minY
        || chunk.y > maxY
      );
      chunk.image.setVisible(visible);
    });
  }

  getTreePreviewSpecs() {
    const previewLayer = this.getLayerByName("TreePreview");
    const plantFirstgid = this.getLatestTilesetFirstgid("whispering-forest-plant.tsx");
    if (!previewLayer?.data?.length || !plantFirstgid) return [];

    const candidates = this.buildTreePreviewCandidates(previewLayer, plantFirstgid);
    if (candidates.length === 0) {
      return [];
    }

    return this.resolveSupportedTreePreviewCandidates(previewLayer, candidates);
  }

  buildTreePreviewCandidates(previewLayer, plantFirstgid) {
    const candidates = [];
    const { width, height, data } = previewLayer;

    for (let tileY = 0; tileY < height; tileY += 1) {
      for (let tileX = 0; tileX < width; tileX += 1) {
        const gid = data[tileY * width + tileX];
        if (!gid) continue;

        TREE_PREVIEW_PATTERNS.forEach((pattern) => {
          if (gid !== plantFirstgid + pattern.localOffset) return;

          const cells = [];
          for (let dy = 0; dy < pattern.heightTiles; dy += 1) {
            for (let dx = 0; dx < pattern.widthTiles; dx += 1) {
              const x = tileX + dx;
              const y = tileY + dy;
              if (x >= width || y >= height) continue;

              cells.push({
                x,
                y,
                expectedGid:
                  plantFirstgid
                  + pattern.localOffset
                  + dx
                  + dy * TILESET_COLUMNS,
              });
            }
          }

          candidates.push({
            id: `preview-tree-${pattern.kind}-${tileX}-${tileY}`,
            kind: pattern.kind,
            x: tileX * TILE_SIZE + pattern.offsetX,
            y: tileY * TILE_SIZE + pattern.offsetY,
            cells,
          });
        });
      }
    }

    return candidates;
  }

  resolveSupportedTreePreviewCandidates(previewLayer, candidates) {
    const activeIds = new Set(candidates.map((candidate) => candidate.id));
    let changed = true;

    while (changed) {
      changed = false;
      const coverage = new Map();

      for (const candidate of candidates) {
        if (!activeIds.has(candidate.id)) continue;

        for (const cell of candidate.cells) {
          const key = `${cell.x},${cell.y}`;
          if (!coverage.has(key)) {
            coverage.set(key, new Set());
          }
          coverage.get(key).add(cell.expectedGid);
        }
      }

      for (const candidate of candidates) {
        if (!activeIds.has(candidate.id)) continue;

        let supported = true;
        for (const cell of candidate.cells) {
          const actualGid = previewLayer.data[cell.y * previewLayer.width + cell.x];
          if (actualGid === cell.expectedGid) {
            continue;
          }

          if (!coverage.get(`${cell.x},${cell.y}`)?.has(actualGid)) {
            supported = false;
            break;
          }
        }

        if (!supported) {
          activeIds.delete(candidate.id);
          changed = true;
        }
      }
    }

    return candidates.filter((candidate) => activeIds.has(candidate.id));
  }

  getBushPreviewSpecs() {
    return this.getSingleTilePreviewSpecs(
      "BushPreview",
      "whispering-forest-plant.tsx",
      BUSH_PREVIEW_TILES,
    );
  }

  getSingleTilePreviewSpecs(layerName, tilesetBasename, previewMappings) {
    const layer = this.getLayerByName(layerName);
    const firstgid = this.getLatestTilesetFirstgid(tilesetBasename);
    if (!layer?.data?.length || !firstgid) return [];

    const gidToKind = new Map(
      Object.entries(previewMappings).map(([kind, mapping]) => [
        firstgid + mapping.localOffset,
        { kind, mapping },
      ]),
    );

    return layer.data.flatMap((gid, index) => {
      const resolved = gidToKind.get(gid);
      if (!resolved) return [];

      const tileX = index % layer.width;
      const tileY = Math.floor(index / layer.width);
      return [{
        kind: resolved.kind,
        x: tileX * TILE_SIZE + resolved.mapping.offsetX,
        y: tileY * TILE_SIZE + resolved.mapping.offsetY,
      }];
    });
  }

  drawDepthSortedTileLayer(layerName) {
    const layer = this.getLayerByName(layerName);
    if (!layer?.data?.length) return;

    layer.data.forEach((gid, index) => {
      if (!gid) return;

      const resolved = this.resolveTilesetForGid(gid);
      if (!resolved) return;

      const tileX = index % layer.width;
      const tileY = Math.floor(index / layer.width);

      this.add
        .image(tileX * TILE_SIZE, tileY * TILE_SIZE, resolved.textureKey, resolved.frame)
        .setOrigin(0)
        .setDepth((tileY + 1) * TILE_SIZE);
    });
  }

  addCollisionObjectBody(collisionObject) {
    const polygon = collisionObject?.polygon;
    if (!Array.isArray(polygon) || polygon.length < 3) return;

    const isExit = (collisionObject.name || "").toLowerCase() === "exit";
    const options = {
      isStatic: true,
      isSensor: isExit,
      label: isExit ? "exit" : undefined,
    };
    const color = isExit ? 0x4fc3f7 : 0xff5a5a;
    const alpha = isExit ? 0.28 : 0.18;

    if (this.isAxisAlignedRectPolygon(polygon)) {
      const xs = polygon.map((point) => point[0]);
      const ys = polygon.map((point) => point[1]);
      const minX = Math.min(...xs);
      const maxX = Math.max(...xs);
      const minY = Math.min(...ys);
      const maxY = Math.max(...ys);
      this.matter.add.rectangle(
        (minX + maxX) / 2,
        (minY + maxY) / 2,
        maxX - minX,
        maxY - minY,
        options,
      );

      if (DEBUG_COLLISIONS) {
        this.drawCollisionDebugBox(
          (minX + maxX) / 2,
          (minY + maxY) / 2,
          maxX - minX,
          maxY - minY,
          color,
          alpha,
        );
      }
      return;
    }

    const body = this.createMatterPolygonBody(polygon, options);
    if (body) {
      this.matter.world.add(body);
    }

    if (DEBUG_COLLISIONS) {
      this.drawCollisionDebugPolygon(polygon, color, alpha);
    }
  }

  isAxisAlignedRectPolygon(polygon) {
    if (!Array.isArray(polygon) || polygon.length !== 4) return false;

    const xs = [...new Set(polygon.map((point) => point[0]))];
    const ys = [...new Set(polygon.map((point) => point[1]))];
    return xs.length === 2 && ys.length === 2;
  }

  createMatterPolygonBody(polygon, options = {}) {
    const matterBodies = Phaser.Physics.Matter.Matter.Bodies;
    const xs = polygon.map((point) => point[0]);
    const ys = polygon.map((point) => point[1]);
    const minX = Math.min(...xs);
    const maxX = Math.max(...xs);
    const minY = Math.min(...ys);
    const maxY = Math.max(...ys);
    const centerX = (minX + maxX) / 2;
    const centerY = (minY + maxY) / 2;
    const localVertices = polygon.map(([x, y]) => ({
      x: x - centerX,
      y: y - centerY,
    }));

    return matterBodies.fromVertices(
      centerX,
      centerY,
      [localVertices],
      options,
      true,
    );
  }

  drawCollisionDebugPolygon(polygon, color = 0xff5a5a, alpha = 0.18) {
    if (!Array.isArray(polygon) || polygon.length < 3) return;

    const gfx = this.add.graphics().setDepth(5001);
    gfx.fillStyle(color, alpha);
    gfx.lineStyle(2, color, Math.min(1, alpha + 0.25));
    gfx.beginPath();
    gfx.moveTo(polygon[0][0], polygon[0][1]);
    polygon.slice(1).forEach(([x, y]) => {
      gfx.lineTo(x, y);
    });
    gfx.closePath();
    gfx.fillPath();
    gfx.strokePath();
  }

  drawForestBackdrop(worldWidth, worldHeight) {
    const bg = this.add.rectangle(0, 0, worldWidth, worldHeight, 0x345226)
      .setOrigin(0)
      .setDepth(0);
    void bg;

    this.drawStaticLayersFromSource();

    const backdrop = this.add.graphics().setDepth(40);
    const bushSpecs = this.getBushPreviewSpecs();
    const treeSpecs = this.getTreePreviewSpecs();
    const exitAnchor = this.getExitAnchorRect();
    const portalRect = this.getVoidCavePortalRect();

    bushSpecs.forEach((bush) => {
      this.add
        .image(bush.x, bush.y, `wf-bush-${bush.kind}`)
        .setOrigin(0, 0)
        .setDepth(bush.y + 4);
    });

    this.drawDepthSortedTileLayer("PropPreview");

    this.getChestSpecs().forEach((chest) => {
      this.createChest(chest);
    });

    treeSpecs.forEach((tree) => {
      const rect = TREE_TEXTURE_RECTS[tree.kind];
      const width = rect.width;
      const height = rect.height;

      backdrop.fillStyle(0x16331d, 0.34);
      backdrop.fillEllipse(
        tree.x + width / 2,
        tree.y + height - 18,
        Math.max(62, width - 30),
        28,
      );

      this.drawTreeSprite(tree.kind, tree.x, tree.y);
    });

    const exitGlow = this.add.graphics().setDepth(200);
    exitGlow.fillStyle(0x66d7ff, 0.18);
    exitGlow.fillEllipse(
      exitAnchor.x + exitAnchor.width / 2,
      exitAnchor.y + exitAnchor.height / 2 + 8,
      Math.max(180, exitAnchor.width + 52),
      Math.max(64, exitAnchor.height + 32),
    );
    exitGlow.fillStyle(0x5ec8ff, 0.65);
    exitGlow.fillRoundedRect(
      exitAnchor.x,
      exitAnchor.y,
      exitAnchor.width,
      Math.max(18, exitAnchor.height * 0.56),
      9,
    );

    this.add
      .text(
        exitAnchor.x + exitAnchor.width / 2,
        exitAnchor.y - 28,
        "Return To Void City",
        {
        font: "18px Arial",
        fill: "#dff8ff",
        stroke: "#123342",
        strokeThickness: 4,
      },
      )
      .setOrigin(0.5)
      .setDepth(210);

    this.matter.add.rectangle(
      portalRect.x + portalRect.width / 2,
      portalRect.y + portalRect.height / 2,
      portalRect.width,
      portalRect.height,
      {
        isStatic: true,
        isSensor: true,
        label: VOID_CAVE_PORTAL_LABEL,
      },
    );

    const cavePortalGlow = this.add.graphics().setDepth(200);
    cavePortalGlow.fillStyle(0x9f93ff, 0.18);
    cavePortalGlow.fillEllipse(
      portalRect.x + portalRect.width / 2,
      portalRect.y + portalRect.height / 2 + 8,
      180,
      64,
    );
    cavePortalGlow.fillStyle(0xb8a6ff, 0.42);
    cavePortalGlow.fillCircle(
      portalRect.x + portalRect.width / 2,
      portalRect.y + portalRect.height / 2,
      28,
    );
    cavePortalGlow.lineStyle(3, 0xf1ebff, 0.92);
    cavePortalGlow.strokeCircle(
      portalRect.x + portalRect.width / 2,
      portalRect.y + portalRect.height / 2,
      18,
    );

    this.add
      .text(
        portalRect.x + portalRect.width / 2,
        portalRect.y - 12,
        "Void Cave",
        {
          font: "18px Arial",
          fill: "#f1ebff",
          stroke: "#2e2552",
          strokeThickness: 4,
        },
      )
      .setOrigin(0.5)
      .setDepth(210);

    this.add
      .text(
        portalRect.x + portalRect.width / 2,
        portalRect.y + portalRect.height / 2 + 6,
        "VC",
        {
          font: "bold 18px Arial",
          fill: "#f7f3ff",
          stroke: "#382f63",
          strokeThickness: 4,
        },
      )
      .setOrigin(0.5)
      .setDepth(210);

    this.add
      .text(56, 48, "Whispering Forest", {
        font: "28px Arial",
        fill: "#eef6e5",
        stroke: "#102215",
        strokeThickness: 6,
      })
      .setOrigin(0, 0.5)
      .setDepth(210)
      .setScrollFactor(0.15);
  }

  drawCollisionDebugBox(x, y, width, height, color = 0xff0000, alpha = 0.2) {
    const g = this.add.graphics().setDepth(5000);
    g.lineStyle(2, color, 0.95);
    g.fillStyle(color, alpha);
    g.strokeRect(x - width / 2, y - height / 2, width, height);
    g.fillRect(x - width / 2, y - height / 2, width, height);
  }

  ensureAtlasTextures() {
    const source = this.textures.get("wfPlantAtlas")?.getSourceImage();
    if (!source) return;

    Object.entries(TREE_TEXTURE_RECTS).forEach(([kind, rect]) => {
      const baseHeight = TREE_LOCKED_BASE_HEIGHT[kind] || 20;
      const canopyHeight = rect.height - baseHeight;
      const canopyKey = `wf-tree-${kind}-canopy`;
      const baseKey = `wf-tree-${kind}-base`;

      if (!this.textures.exists(canopyKey)) {
        const canopyTexture = this.textures.createCanvas(
          canopyKey,
          rect.width,
          canopyHeight,
        );
        const canopyCtx = canopyTexture.context;
        canopyCtx.drawImage(
          source,
          rect.x,
          rect.y,
          rect.width,
          canopyHeight,
          0,
          0,
          rect.width,
          canopyHeight,
        );
        canopyTexture.refresh();
      }

      if (!this.textures.exists(baseKey)) {
        const baseTexture = this.textures.createCanvas(
          baseKey,
          rect.width,
          baseHeight,
        );
        const baseCtx = baseTexture.context;
        baseCtx.drawImage(
          source,
          rect.x,
          rect.y + canopyHeight,
          rect.width,
          baseHeight,
          0,
          0,
          rect.width,
          baseHeight,
        );
        baseTexture.refresh();
      }
    });

    Object.entries(BUSH_TEXTURE_RECTS).forEach(([kind, rect]) => {
      const key = `wf-bush-${kind}`;
      if (this.textures.exists(key)) return;

      const canvasTexture = this.textures.createCanvas(
        key,
        rect.width,
        rect.height,
      );
      const ctx = canvasTexture.context;
      ctx.drawImage(
        source,
        rect.x,
        rect.y,
        rect.width,
        rect.height,
        0,
        0,
        rect.width,
        rect.height,
      );
      canvasTexture.refresh();
    });

    const propSource = this.textures.get("wfPropsAtlas")?.getSourceImage();
    if (!propSource) return;

    Object.entries(CHEST_TEXTURE_RECTS).forEach(([kind, rect]) => {
      const key = `wf-chest-${kind}`;
      if (this.textures.exists(key)) return;

      const canvasTexture = this.textures.createCanvas(
        key,
        rect.width,
        rect.height,
      );
      const ctx = canvasTexture.context;
      ctx.drawImage(
        propSource,
        rect.x,
        rect.y,
        rect.width,
        rect.height,
        0,
        0,
        rect.width,
        rect.height,
      );
      canvasTexture.refresh();
    });
  }

  ensureAmbientParticleTextures() {
    if (!this.textures.exists("wfAmbientSpore")) {
      const sporeTexture = this.textures.createCanvas("wfAmbientSpore", 12, 12);
      const sporeCtx = sporeTexture.context;
      const gradient = sporeCtx.createRadialGradient(6, 6, 1, 6, 6, 6);
      gradient.addColorStop(0, "rgba(241, 255, 226, 0.95)");
      gradient.addColorStop(0.45, "rgba(189, 223, 167, 0.55)");
      gradient.addColorStop(1, "rgba(189, 223, 167, 0)");
      sporeCtx.fillStyle = gradient;
      sporeCtx.fillRect(0, 0, 12, 12);
      sporeTexture.refresh();
    }

    if (!this.textures.exists("wfAmbientLeaf")) {
      const leafTexture = this.textures.createCanvas("wfAmbientLeaf", 12, 8);
      const leafCtx = leafTexture.context;
      leafCtx.fillStyle = "rgba(148, 170, 77, 0.95)";
      leafCtx.beginPath();
      leafCtx.ellipse(6, 4, 4.6, 2.4, -0.35, 0, Math.PI * 2);
      leafCtx.fill();
      leafCtx.strokeStyle = "rgba(102, 120, 39, 0.7)";
      leafCtx.lineWidth = 1;
      leafCtx.beginPath();
      leafCtx.moveTo(3.2, 5.2);
      leafCtx.lineTo(8.4, 2.6);
      leafCtx.stroke();
      leafTexture.refresh();
    }
  }

  createAmbientForestLife(worldWidth, worldHeight) {
    const spores = this.add.particles(0, 0, "wfAmbientSpore", {
      x: { min: 80, max: worldWidth - 80 },
      y: { min: 72, max: worldHeight - 120 },
      lifespan: { min: 3200, max: 5400 },
      speedX: { min: -6, max: 6 },
      speedY: { min: -12, max: -3 },
      scale: { start: 0.42, end: 0.12 },
      alpha: { start: 0.2, end: 0 },
      blendMode: "ADD",
      frequency: 650,
      quantity: 1,
    });
    spores.setDepth(FOREST_AMBIENT_DEPTH);

    const leaves = this.add.particles(0, 0, "wfAmbientLeaf", {
      x: { min: 48, max: worldWidth - 48 },
      y: { min: 48, max: worldHeight - 140 },
      lifespan: { min: 4600, max: 6800 },
      speedX: { min: -14, max: 10 },
      speedY: { min: 4, max: 10 },
      rotate: { min: -18, max: 18 },
      angularVelocity: { min: -9, max: 9 },
      scale: { start: 0.42, end: 0.24 },
      alpha: { start: 0.18, end: 0 },
      frequency: 1100,
      quantity: 1,
    });
    leaves.setDepth(FOREST_AMBIENT_DEPTH - 2);
  }

  ensureNpcAnimations() {
    this.npcPatrolConfigs.forEach((npc) => {
      ["down", "left", "right", "up"].forEach((dir, i) => {
        const walkKey = `${npc.key}-walk-${dir}`;
        if (!this.anims.exists(walkKey)) {
          this.anims.create({
            key: walkKey,
            frames: this.anims.generateFrameNumbers(npc.texture, {
              start: 4 * i,
              end: 4 * i + 3,
            }),
            frameRate: 6,
            repeat: -1,
          });
        }

        const idleKey = `${npc.key}-idle-${dir}`;
        if (!this.anims.exists(idleKey)) {
          this.anims.create({
            key: idleKey,
            frames: this.anims.generateFrameNumbers(npc.idleTexture, {
              start: 4 * i,
              end: 4 * i + 3,
            }),
            frameRate: 4,
            repeat: -1,
          });
        }
      });
    });
  }

  createPatrolNpcs() {
    this.npcPatrolConfigs.forEach((config) => {
      const sprite = this.add
        .sprite(config.start.x, config.start.y, config.idleTexture, 0)
        .setOrigin(0.5, 1)
        .setScale(2.35)
        .setDepth(config.start.y + 2);

      const body = this.matter.add.rectangle(
        config.start.x,
        config.start.y - 12,
        28,
        26,
        {
          isStatic: true,
          isSensor: true,
          label: `npc:${config.key}`,
        },
      );

      this.patrolNpcs.push({
        ...config,
        sprite,
        body,
        pathIndex: 1 % config.path.length,
        waitUntil: 0,
        lastDirection: "down",
        debugGfx: DEBUG_COLLISIONS
          ? this.add.graphics().setDepth(5001)
          : null,
      });

      this.setNpcIdle(this.patrolNpcs[this.patrolNpcs.length - 1], "down");
    });
  }

  setNpcIdle(npc, direction = npc.lastDirection || "down") {
    npc.lastDirection = direction;
    const animKey = `${npc.key}-idle-${direction}`;
    if (npc.sprite.anims.currentAnim?.key !== animKey) {
      npc.sprite.play(animKey, true);
    }
  }

  showNpcChatBubble(npc) {
    if (!npc) return;

    const message = npc.speech || "The forest is listening.";
    npc.waitUntil = Math.max(npc.waitUntil || 0, this.time.now + NPC_CHAT_BUBBLE_MS);
    npc.talkingUntil = this.time.now + NPC_CHAT_BUBBLE_MS;
    this.setNpcIdle(npc);

    if (!this.chatBubble) {
      this.chatBubble = this.add.container(0, 0).setDepth(4100);
      const bubbleBg = this.add.graphics();
      const pointer = this.add.graphics();
      const text = this.add.text(0, 0, "", {
        font: "16px Arial",
        fill: "#1d1b32",
        align: "center",
        wordWrap: { width: 190, useAdvancedWrap: true },
      });
      text.setOrigin(0.5, 0.5);
      this.chatBubble.add([bubbleBg, pointer, text]);
      this.chatBubble.bg = bubbleBg;
      this.chatBubble.pointer = pointer;
      this.chatBubbleText = text;
      this.chatBubble.setVisible(false);
    }

    this.chatBubbleTarget = npc;
    this.chatBubbleText.setText(message);
    const paddingX = 16;
    const paddingY = 12;
    const width = this.chatBubbleText.width + paddingX * 2;
    const height = this.chatBubbleText.height + paddingY * 2;

    this.chatBubble.bg.clear();
    this.chatBubble.bg.fillStyle(0xf8f2ff, 0.96);
    this.chatBubble.bg.lineStyle(3, 0x31461a, 1);
    this.chatBubble.bg.fillRoundedRect(-width / 2, -height / 2, width, height, 12);
    this.chatBubble.bg.strokeRoundedRect(-width / 2, -height / 2, width, height, 12);

    this.chatBubble.pointer.clear();
    this.chatBubble.pointer.fillStyle(0xf8f2ff, 0.96);
    this.chatBubble.pointer.lineStyle(3, 0x31461a, 1);
    this.chatBubble.pointer.beginPath();
    this.chatBubble.pointer.moveTo(-10, height / 2 - 2);
    this.chatBubble.pointer.lineTo(0, height / 2 + 14);
    this.chatBubble.pointer.lineTo(10, height / 2 - 2);
    this.chatBubble.pointer.closePath();
    this.chatBubble.pointer.fillPath();
    this.chatBubble.pointer.strokePath();

    this.chatBubble.setVisible(true);
    this.updateNpcChatBubblePosition();

    this.chatBubbleHideTimer?.remove(false);
    this.chatBubbleHideTimer = this.time.delayedCall(NPC_CHAT_BUBBLE_MS, () => {
      this.hideNpcChatBubble(npc);
    });
  }

  hideNpcChatBubble(npc = null) {
    if (npc && this.chatBubbleTarget !== npc) return;
    this.chatBubbleTarget = null;
    this.chatBubbleHideTimer = null;
    this.chatBubble?.setVisible(false);
  }

  updateNpcChatBubblePosition() {
    if (!this.chatBubble?.visible || !this.chatBubbleTarget?.sprite) return;

    this.chatBubble.setPosition(
      this.chatBubbleTarget.sprite.x,
      this.chatBubbleTarget.sprite.y - 78,
    );
  }

  setNpcInteractionTarget(npc) {
    if (this.currentInteractable || this.currentDoor) {
      this.currentNpc = npc;
      return;
    }
    if (this.currentNpc === npc) return;

    this.currentNpc = npc;
    if (npc) {
      this.setInteractPrompt(npc.promptLabel || "Talk");
      return;
    }

    this.clearInteractPrompt();
  }

  updatePatrolNpcs() {
    let nearestNpc = null;
    let nearestDistanceSq = Infinity;

    this.patrolNpcs.forEach((npc) => {
      const npcLeft = npc.sprite.x - 14;
      const npcTop = npc.sprite.y - 25;
      const npcWidth = 28;
      const npcHeight = 26;
      const playerLeft = this.player.x - 22.5;
      const playerTop = this.player.y - 32.5;
      const playerWidth = 45;
      const playerHeight = 65;
      const touchingPlayer = !(
        playerLeft + playerWidth < npcLeft
        || npcLeft + npcWidth < playerLeft
        || playerTop + playerHeight < npcTop
        || npcTop + npcHeight < playerTop
      );

      if (npc.debugGfx) {
        npc.debugGfx.clear();
        npc.debugGfx.lineStyle(2, 0xb388ff, 0.95);
        npc.debugGfx.fillStyle(0xb388ff, 0.12);
        npc.debugGfx.strokeRect(npcLeft, npcTop, npcWidth, npcHeight);
        npc.debugGfx.fillRect(npcLeft, npcTop, npcWidth, npcHeight);
      }

      if (touchingPlayer) {
        const distanceSq =
          (this.player.x - npc.sprite.x) ** 2 + (this.player.y - npc.sprite.y) ** 2;
        if (distanceSq < nearestDistanceSq) {
          nearestDistanceSq = distanceSq;
          nearestNpc = npc;
        }
        this.setNpcIdle(npc);
        return;
      }

      if (this.currentNpc === npc) {
        this.hideNpcChatBubble(npc);
      }

      if (npc.talkingUntil > this.time.now) {
        this.setNpcIdle(npc);
        return;
      }

      if (npc.waitUntil > this.time.now) {
        this.setNpcIdle(npc);
        return;
      }

      const target = npc.path[npc.pathIndex];
      if (!target) return;

      const dx = target.x - npc.sprite.x;
      const dy = target.y - npc.sprite.y;
      const distance = Math.hypot(dx, dy);

      if (distance < NPC_MOVE_SPEED + 0.2) {
        npc.sprite.setPosition(target.x, target.y);
        Phaser.Physics.Matter.Matter.Body.setPosition(npc.body, {
          x: target.x,
          y: target.y - 12,
        });
        npc.pathIndex = (npc.pathIndex + 1) % npc.path.length;
        npc.waitUntil = this.time.now + NPC_WAIT_MS;
        this.setNpcIdle(npc);
        return;
      }

      const vx = (dx / distance) * NPC_MOVE_SPEED;
      const vy = (dy / distance) * NPC_MOVE_SPEED;
      npc.sprite.x += vx;
      npc.sprite.y += vy;
      npc.sprite.setDepth(npc.sprite.y + 2);
      Phaser.Physics.Matter.Matter.Body.setPosition(npc.body, {
        x: npc.sprite.x,
        y: npc.sprite.y - 12,
      });

      if (Math.abs(dy) > Math.abs(dx)) {
        npc.lastDirection = vy < 0 ? "up" : "down";
        npc.sprite.anims.play(
          vy < 0 ? `${npc.key}-walk-up` : `${npc.key}-walk-down`,
          true,
        );
      } else if (vx < 0) {
        npc.lastDirection = "left";
        npc.sprite.anims.play(`${npc.key}-walk-left`, true);
      } else {
        npc.lastDirection = "right";
        npc.sprite.anims.play(`${npc.key}-walk-right`, true);
      }
    });

    this.setNpcInteractionTarget(nearestNpc);
  }

  drawTreeSprite(kind, x, y) {
    const rect = TREE_TEXTURE_RECTS[kind];
    const pivot = TREE_PIVOT_POINTS[kind] || {
      x: rect.width / 2,
      y: rect.height,
    };
    const baseHeight = TREE_LOCKED_BASE_HEIGHT[kind] || 20;
    const canopyHeight = rect.height - baseHeight;
    const seamX = x + pivot.x;
    const seamY = y + canopyHeight;

    const baseImage = this.add
      .image(x, y + canopyHeight, `wf-tree-${kind}-base`)
      .setOrigin(0, 0)
      .setDepth(y + rect.height - 9);

    const container = this.add.container(seamX, seamY).setDepth(y + rect.height - 10);
    const canopy = this.add
      .image(-pivot.x, -canopyHeight, `wf-tree-${kind}-canopy`)
      .setOrigin(0, 0);
    container.add(canopy);

    this.treeSprites.push({
      kind,
      x,
      y,
      width: rect.width,
      height: rect.height,
      baseImage,
      container,
      canopy,
      sway: TREE_SWAY_SETTINGS[kind] || TREE_SWAY_SETTINGS.medium,
      phaseMs: this.treeSprites.length * 170,
      restY: -canopyHeight,
    });
  }

  startTreeWindSway() {
    this.updateTreeVisibility();
    this.updateVisibleTreeSway(this.time.now);
  }

  updateTreeVisibility() {
    if (!this.treeSprites.length || !this.cameras?.main) return;

    const view = this.cameras.main.worldView;
    const cullMinX = view.x - TREE_CULL_PADDING;
    const cullMaxX = view.right + TREE_CULL_PADDING;
    const cullMinY = view.y - TREE_CULL_PADDING;
    const cullMaxY = view.bottom + TREE_CULL_PADDING;
    const swayMinX = view.x - TREE_SWAY_PADDING;
    const swayMaxX = view.right + TREE_SWAY_PADDING;
    const swayMinY = view.y - TREE_SWAY_PADDING;
    const swayMaxY = view.bottom + TREE_SWAY_PADDING;
    this.visibleTreeSprites = [];

    this.treeSprites.forEach((tree) => {
      const visible = !(
        tree.x + tree.width < cullMinX
        || tree.x > cullMaxX
        || tree.y + tree.height < cullMinY
        || tree.y > cullMaxY
      );
      tree.baseImage.setVisible(visible);
      tree.container.setVisible(visible);
      if (!visible) {
        tree.canopy.y = tree.restY;
        return;
      }

      const shouldSway = !(
        tree.x + tree.width < swayMinX
        || tree.x > swayMaxX
        || tree.y + tree.height < swayMinY
        || tree.y > swayMaxY
      );
      if (!shouldSway) {
        tree.canopy.y = tree.restY;
        return;
      }

      this.visibleTreeSprites.push(tree);
    });
  }

  updateVisibleTreeSway(time = 0) {
    if (!this.visibleTreeSprites.length) return;

    this.visibleTreeSprites.forEach((tree) => {
      const wave = Math.sin(
        ((time + tree.phaseMs) * Math.PI * 2) / tree.sway.duration,
      );
      tree.canopy.y = tree.restY + wave * tree.sway.bob;
    });
  }

  createChest(chest) {
    const rect = CHEST_TEXTURE_RECTS.closed;
    const isOpened = this.shouldUseStoryRandomPackChests()
      ? Boolean(chest.opened)
      : Boolean(this.openedChests?.[chest.id]);
    const spriteY = isOpened
      ? chest.y - (CHEST_TEXTURE_RECTS.open.height - CHEST_TEXTURE_RECTS.closed.height)
      : chest.y;
    const sprite = this.add
      .image(chest.x, spriteY, isOpened ? "wf-chest-open" : "wf-chest-closed")
      .setOrigin(0, 0)
      .setDepth(chest.y + rect.height - 4);

    const { collisionBody, interactionBody } = createStoryChestBodies(
      this,
      chest,
      rect,
      { isOpened },
    );

    this.chestSprites.set(chest.id, {
      ...chest,
      sprite,
      collisionBody,
      opened: isOpened,
    });
    if (interactionBody) {
      this.chestBodies.set(chest.id, interactionBody);
    }

    if (DEBUG_COLLISIONS) {
      const collisionRect = getStoryChestCollisionRect(chest, rect);
      this.drawCollisionDebugBox(
        collisionRect.x,
        collisionRect.y,
        collisionRect.width,
        collisionRect.height,
        0xff3355,
        0.35,
      );
      if (interactionBody) {
        const interactionRect = getStoryChestInteractionRect(chest, rect);
        this.drawCollisionDebugBox(
          interactionRect.x,
          interactionRect.y,
          interactionRect.width,
          interactionRect.height,
          0x33ddff,
          0.08,
        );
      }
    }
  }

  openChest(chestId) {
    const chest = this.chestSprites.get(chestId);
    const body = this.chestBodies.get(chestId);
    if (!chest || chest.opened) return;

    chest.sprite.setTexture("wf-chest-open");
    chest.sprite.setY(
      chest.y - (CHEST_TEXTURE_RECTS.open.height - CHEST_TEXTURE_RECTS.closed.height),
    );
    chest.sprite.setDepth(chest.y + CHEST_TEXTURE_RECTS.closed.height - 4);
    chest.opened = true;
    if (this.shouldUseStoryRandomPackChests()) {
      markWhisperingForestStoryChestOpened(chestId);
    }
    this.currentInteractable = null;
    this.clearInteractPrompt();

    if (body) {
      this.matter.world.remove(body);
      this.chestBodies.delete(chestId);
    }

    this.tweens.add({
      targets: chest.sprite,
      scaleX: { from: 0.92, to: 1 },
      scaleY: { from: 0.92, to: 1 },
      y: { from: chest.sprite.y + 4, to: chest.sprite.y },
      duration: 180,
      ease: "Back.Out",
      onComplete: () => {
        window.dispatchEvent(
          new CustomEvent(
            "whispering-forest:chest-open-animation-complete",
            {
              detail: { chestId },
            },
          ),
        );
      },
    });
  }

  requestOpenChest(chestId) {
    const chest = this.chestSprites.get(chestId);
    if (!chest || chest.opened || this.pendingChestOpenId) return;

    this.pendingChestOpenId = chestId;
    window.dispatchEvent(
      new CustomEvent("whispering-forest:chest-request-open", {
        detail: {
          chestId,
          chestMode: chest.chestMode || null,
          rewardCardId: chest.rewardCard?.id || null,
          rewardCard: chest.rewardCard || null,
        },
      }),
    );
  }

  setInteractPrompt(label) {
    window.dispatchEvent(
      new CustomEvent("interact-proximity", { detail: true }),
    );
    window.dispatchEvent(new CustomEvent("door-name", { detail: label }));
  }

  clearInteractPrompt() {
    window.dispatchEvent(
      new CustomEvent("interact-proximity", { detail: false }),
    );
    window.dispatchEvent(new CustomEvent("door-name", { detail: null }));
  }

  update() {
    this.syncStoryMissionRuntime();
    this.syncStoryRuneStoneInteraction();
    this.syncStoryRuneStoneTeleportEffect();
    this.updatePatrolNpcs();
    this.updateNpcChatBubblePosition();
    this.updateGroundChunkVisibility();
    this.updateTreeVisibility();

    const speed = 2;
    let vx = 0;
    let vy = 0;
    const { x: jx, y: jy } = this.joystick;
    const sceneInputLocked =
      typeof window !== "undefined" &&
      (
        Boolean(window.__storySceneInputLocked) ||
        Boolean(window.__storyWorldEncounterPaused)
      );

    if (!sceneInputLocked && (jx || jy)) {
      vx = jx * speed;
      vy = jy * speed;
    } else if (!sceneInputLocked) {
      if (this.cursors.left.isDown || this.keys.A.isDown) vx = -speed;
      else if (this.cursors.right.isDown || this.keys.D.isDown) vx = speed;
      if (this.cursors.up.isDown || this.keys.W.isDown) vy = -speed;
      else if (this.cursors.down.isDown || this.keys.S.isDown) vy = speed;
    }

    if (vx && vy) {
      if (Math.abs(vx) >= Math.abs(vy)) {
        vy = 0;
      } else {
        vx = 0;
      }
    }

    this.player.setVelocity(vx, vy);
    this.player.setDepth(this.player.y + 24);
    this.updateStoryEncounterSprites();
    this.updateVisibleTreeSway(this.time.now);

    if (this.playerDebugGfx && this.player?.body?.bounds) {
      const bounds = this.player.body.bounds;
      const width = bounds.max.x - bounds.min.x;
      const height = bounds.max.y - bounds.min.y;

      this.playerDebugGfx.clear();
      this.playerDebugGfx.lineStyle(2, 0x7bf1a8, 0.95);
      this.playerDebugGfx.fillStyle(0x7bf1a8, 0.12);
      this.playerDebugGfx.strokeRect(bounds.min.x, bounds.min.y, width, height);
      this.playerDebugGfx.fillRect(bounds.min.x, bounds.min.y, width, height);
    }

    if (
      updateLocalOrbAvatar(
        this,
        this.player,
        this.playerOrbImage,
        this.localOrbAvatarData,
        {
          x: this.player.x,
          y: this.player.y,
          vx,
          depth: this.player.y + 24,
        },
      )
    ) {
      // Orb avatars use the image overlay instead of the walking spritesheet.
    } else if (Math.abs(vy) > Math.abs(vx)) {
      if (vy < 0) this.player.anims.play("walk-up", true);
      else if (vy > 0) this.player.anims.play("walk-down", true);
    } else if (vx < 0) {
      this.player.anims.play("walk-left", true);
    } else if (vx > 0) {
      this.player.anims.play("walk-right", true);
    } else {
      this.player.anims.stop();
    }

    const direction =
      Math.abs(vy) > Math.abs(vx)
        ? vy < 0
          ? 0
          : 180
        : vx < 0
          ? 270
          : vx > 0
            ? 90
            : 180;
    this.lastFacingDirection = direction;

    syncStoryCompanionFollower(this, this.storyCompanionFollower, {
      vx,
      vy,
      direction,
    });

    const onLocalMove =
      typeof window !== "undefined" ? window.__voidCityOnLocalMove : null;
    if (typeof onLocalMove === "function") {
      const animState = vx === 0 && vy === 0 ? "idle" : "walking";
      const shouldBroadcast =
        this.player.x !== this.lastBroadcast.x ||
        this.player.y !== this.lastBroadcast.y ||
        direction !== this.lastBroadcast.direction ||
        animState !== this.lastBroadcast.animState;

      if (shouldBroadcast) {
        this.lastBroadcast = {
          x: this.player.x,
          y: this.player.y,
          direction,
          animState,
        };
        onLocalMove(this.player.x, this.player.y, direction, animState);
      }
    }
  }
}
